// Reusable page-link picker for Editor.js block tools (vanilla DOM — tools own
// their markup). A dropdown of the tenant's pages plus active-module pages
// (options fetched lazily via `listOptions`) with a "Custom URL…" fallback
// that reveals a free-text input. Emits the chosen site-relative or custom URL
// through `onChange`. Styled by block-editor.css (.pgs-link-field*).

const CUSTOM = "__custom__";
const NONE = "";

/**
 * @param {Object} props
 * @param {string} props.value            Current href ("" = no link yet).
 * @param {() => Promise<Array<{url: string, label: string, published: boolean}>>} props.listOptions
 * @param {(href: string) => void} props.onChange
 * @returns {HTMLElement}
 */
export function createPageLinkField({ value, listOptions, onChange }) {
  const root = document.createElement("div");
  root.className = "pgs-link-field";

  const select = document.createElement("select");
  select.className = "pgs-link-field__select";
  select.setAttribute("aria-label", "Link to");

  const input = document.createElement("input");
  input.type = "text";
  input.className = "pgs-link-field__input";
  input.placeholder = "https://… or /contact/";
  input.value = value || "";
  input.setAttribute("aria-label", "Custom URL");

  const syncInputVisibility = () => {
    input.style.display = select.value === CUSTOM ? "" : "none";
  };

  const rebuild = (options, current) => {
    select.innerHTML = "";

    const placeholder = document.createElement("option");
    placeholder.value = NONE;
    placeholder.textContent = "Choose where it links…";
    select.appendChild(placeholder);

    const urls = [];
    for (const option of options) {
      const el = document.createElement("option");
      el.value = option.url;
      el.textContent = option.published ? option.label : `${option.label} (draft)`;
      urls.push(option.url);
      select.appendChild(el);
    }

    const custom = document.createElement("option");
    custom.value = CUSTOM;
    custom.textContent = "Custom URL…";
    select.appendChild(custom);

    if (!current) select.value = NONE;
    else select.value = urls.includes(current) ? current : CUSTOM;
    syncInputVisibility();
  };

  // Options arrive async; until then the stored value stays editable as a
  // custom URL so the field is never a dead end.
  rebuild([], value || "");
  listOptions()
    .then((options) => rebuild(options ?? [], input.value.trim()))
    .catch(() => {
      // Page lookup failed — the custom-URL path keeps working.
    });

  select.addEventListener("change", () => {
    syncInputVisibility();
    if (select.value === CUSTOM) {
      input.focus();
      onChange(input.value.trim());
    } else {
      onChange(select.value);
    }
  });
  input.addEventListener("input", () => onChange(input.value.trim()));

  root.appendChild(select);
  root.appendChild(input);
  return root;
}
